/**
 * Placement Links tab component for managing share URLs.
 */

import { useState } from 'react';
import { useCreateShareUrl } from '@/hooks/usePlacements';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Copy, Plus, ExternalLink } from 'lucide-react';
import type { ShareUrlRequest } from '@/api/schemas';
import './links.scss';

interface PlacementLinksProps {
  placementId: string;
  trackingLink: string;
}

export function PlacementLinks({ placementId, trackingLink }: PlacementLinksProps) {
  const createShareUrl = useCreateShareUrl();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [createdUrls, setCreatedUrls] = useState<string[]>([]);

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const handleCreate = async () => {
    try {
      const data: ShareUrlRequest = { post_url: postUrl || '' };
      const result = await createShareUrl.mutateAsync({
        placementId,
        data,
      });
      setCreatedUrls([...createdUrls, result.url]);
      setPostUrl('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create share URL:', error);
    }
  };

  return (
    <div className="placement-links">
      <div className="placement-links__header">
        <h3 className="h3">Propagation Links</h3>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
          Create Share URL
        </Button>
      </div>

      {showCreateForm && (
        <Card>
          <div className="placement-links__create-form">
            <label htmlFor="post-url" className="label">
              Post URL (optional)
            </label>
            <input
              id="post-url"
              type="url"
              value={postUrl}
              onChange={(e) => setPostUrl(e.target.value)}
              placeholder="https://x.com/user/status/12345"
              className="input"
            />
            <div className="placement-links__create-actions">
              <Button onClick={handleCreate} disabled={createShareUrl.isPending}>
                {createShareUrl.isPending ? 'Creating...' : 'Create'}
              </Button>
              <Button variant="secondary" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {createdUrls.length > 0 ? (
        <div className="placement-links__list">
          {createdUrls.map((url, index) => (
            <Card key={index}>
              <div className="placement-links__item">
                <div className="placement-links__item-info">
                  <code className="placement-links__item-url">{url}</code>
                </div>
                <div className="placement-links__item-actions">
                  <Button
                    variant="secondary"
                    onClick={() => handleCopy(url)}
                    title="Copy link"
                  >
                    <Copy style={{ width: '1rem', height: '1rem' }} />
                  </Button>
                  <Button
                    variant="secondary"
                    as="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink style={{ width: '1rem', height: '1rem' }} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <p className="of-text-muted">No share URLs created yet. Create one to generate a propagation link.</p>
        </Card>
      )}
    </div>
  );
}

